import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

interface WorkflowTemplate {
  id: string;
  name: string;
  entity_type: string;
  entity_type_display: string;
  event_type: string;
  event_type_display: string;
  priority: number;
  priority_display: string;
}

interface Initiator {
  id: string;
  full_name: string;
  username: string;
  email: string;
}

interface WorkflowTransaction {
  id: string;
  transaction_id: string;
  workflow_template: WorkflowTemplate;
  initiator: Initiator;
  status: string;
  status_display: string;
  current_approval_level: number;
  reference_object_type: string;
  reference_object_id: string;
  submission_date: string;
  estimated_completion_date: string | null;
  completion_date: string | null;
  form_data: Record<string, string | number | boolean | null>;
  form_data_snapshot: Record<string, string | number | boolean | null>;
}

interface ApprovalLevel {
  id: string;
  level: number;
  description: string;
  approval_mode: string;
  approval_mode_display: string;
  approval_type: 'sequential';
  approval_type_display: string;
}

interface ApprovalTask {
  id: string;
  approval_level: ApprovalLevel;
  status: string;
  status_display: string;
  assigned_date: string | null;
  due_date: string | null;
  completed_date: string | null;
  is_current: boolean;
  assigned_to_user: Initiator | null;
  assigned_to_role: string | null;
  assigned_to_role_name?: string | null;
  completed_by: Initiator | null;
  comments: string | null;
}

interface HistoryEntry {
  id: string;
  action_type: string;
  action_type_display: string;
  action_date: string;
  user: Initiator;
  approval_level: number;
  comments: string;
  metadata: Record<string, string | number | boolean | null>;
}

interface Progress {
  total_levels: number;
  completed_levels: number;
  percentage: number;
}

interface WorkflowLevelProgress {
  id: string;
  level: number;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  status_display: string;
}

interface SLA {
  is_compliant: boolean;
  estimated_completion: string | null;
  time_remaining: string | null;
  time_elapsed: string;
}

export interface TransactionDetailResponse {
  transaction: WorkflowTransaction;
  tasks: ApprovalTask[];
  levels: WorkflowLevelProgress[];
  history: HistoryEntry[];
  progress: Progress;
  sla: SLA;
}

// Define the WorkflowTransactionHistory interface
export interface WorkflowTransactionHistory {
  id: string;
  transaction_id: string;
  action_type: 'submission' | 'approval' | 'rejection' | 'escalation' | 'reassignment' | 'level_change';
  action_type_display: string;
  action_date: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  approval_level?: number;
  comments?: string;
  metadata?: Record<string, unknown>;
}

// Define the WorkflowTransactionStatus interface
export interface WorkflowTransactionStatus {
  id: string;
  transaction_id: string;
  current_approval_level: number;
  level_name?: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_progress';
  current_approvers: {
    id: string;
    name: string;
    role?: string;
  }[];
  progress_percentage: number;
  estimated_completion_date: string | null;
  sla_status: 'on_track' | 'at_risk' | 'overdue';
  time_spent_at_current_level: string;
  total_time_elapsed: string;
}

export interface UpdateWorkflowTransactionResponse {
  message: string;
  data: {
    id: string;
    transaction_id: string;
    workflow_template: {
      id: string;
      name: string;
    };
    initiator: {
      id: string;
      name: string;
    };
    status: string;
    status_display: string;
    current_approval_level: number;
    form_data: {
      company: string;
      department_code: string;
      name: string;
      description: string;
      contact_person: string;
    };
    reference_object_type: string;
    reference_object_id: string;
    submission_date: string; // ISO timestamp
    estimated_completion_date: string | null;
    completion_date: string | null;
  };
  changed_fields?: string[];
}


// Define the WorkflowAnalytics interface
export interface WorkflowAnalytics {
  workflow_template: string;
  template_name: string;
  time_period: 'daily' | 'weekly' | 'monthly';
  execution_count: number;
  average_completion_time: string;
  approval_rate: number;
  rejection_rate: number;
  sla_compliance_rate: number;
  bottleneck_level?: number;
  bottleneck_level_name?: string;
  created_at: string;
}

// Define the RejectionAnalysis interface
export interface RejectionAnalysis {
  workflow_template: string;
  template_name: string;
  time_period: 'daily' | 'weekly' | 'monthly';
  total_rejections: number;
  rejection_categories: {
    category: string;
    count: number;
    percentage: number;
  }[];
  common_terms: {
    term: string;
    count: number;
  }[];
  created_at: string;
}

// Pagination interface for workflow transactions
export interface WorkflowTransactionPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowTransaction[];
}

// Pagination interface for workflow transaction history
export interface WorkflowTransactionHistoryPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowTransactionHistory[];
}

// Filter parameters for workflow transactions
export interface WorkflowTransactionFilterParams {
  status?: 'pending' | 'approved' | 'rejected' | 'in_progress';
  form_type?: string;
  initiated_by?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

// Filter parameters for workflow analytics
export interface WorkflowAnalyticsFilterParams {
  workflow_template?: string;
  time_period?: 'daily' | 'weekly' | 'monthly';
  date_from?: string;
  date_to?: string;
}

// Create API
export const workflowTransactionApi = createApi({
  reducerPath: 'workflowTransactionApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowTransaction', 'WorkflowTransactionHistory', 'WorkflowAnalytics'],
  endpoints: (builder) => ({
    // Get workflow transactions
    getWorkflowTransactions: builder.query<WorkflowTransactionPaginatedResponse, WorkflowTransactionFilterParams | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return { url: 'workflow-transactions/', params: queryParams };
      },
      providesTags: (result) => 
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'WorkflowTransaction' as const, id })),
              { type: 'WorkflowTransaction', id: 'LIST' },
            ]
          : [{ type: 'WorkflowTransaction', id: 'LIST' }],
    }),
    
    // Get workflow transaction by ID
    getWorkflowTransactionById: builder.query<TransactionDetailResponse, string>({
      query: (id) => `workflow-transactions/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransaction', id }],
    }),
    
    // Get workflow transaction status
    getWorkflowTransactionStatus: builder.query<WorkflowTransactionStatus, string>({
      query: (id) => `workflow-transactions/${id}/status/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransaction', id }],
    }),
    
    // Get workflow transaction history
    getWorkflowTransactionHistory: builder.query<WorkflowTransactionHistoryPaginatedResponse, { id: string; page?: number; limit?: number }>({
      query: ({ id, page = 1, limit = 10 }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        return { url: `workflow-transactions/${id}/history/`, params: queryParams };
      },
      providesTags: (result, error, { id }) => [
        { type: 'WorkflowTransactionHistory', id },
        { type: 'WorkflowTransactionHistory', id: 'LIST' },
      ],
    }),
    
    // Get workflow transaction active level
    getWorkflowTransactionActiveLevel: builder.query<{ level: number; approvers: { id: string; name: string }[] }, string>({
      query: (id) => `workflow-transactions/${id}/active-level/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransaction', id }],
    }),
    
    // Get workflow transaction SLA information
    getWorkflowTransactionSla: builder.query<{ 
      status: 'on_track' | 'at_risk' | 'overdue';
      estimated_completion: string | null;
      time_remaining: string | null;
    }, string>({
      query: (id) => `workflow-transactions/${id}/sla/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransaction', id }],
    }),
    
    // Export workflow transaction history
    exportWorkflowTransactionHistory: builder.mutation<{ file_url: string }, { 
      id: string; 
      format: 'csv' | 'pdf' | 'excel';
      date_from?: string;
      date_to?: string;
    }>({
      query: ({ id, format, date_from, date_to }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('format', format);
        if (date_from) queryParams.append('date_from', date_from);
        if (date_to) queryParams.append('date_to', date_to);
        return {
          url: `workflow-transactions/${id}/export/`,
          method: 'GET',
          params: queryParams,
        };
      },
    }),
    
    // Get workflow analytics
    getWorkflowAnalytics: builder.query<WorkflowAnalytics[], WorkflowAnalyticsFilterParams | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return { url: 'workflow-analytics/', params: queryParams };
      },
      providesTags: [{ type: 'WorkflowAnalytics', id: 'LIST' }],
    }),
    
    // Get workflow analytics for a specific template
    getWorkflowAnalyticsByTemplate: builder.query<WorkflowAnalytics, { 
      templateId: string;
      time_period?: 'daily' | 'weekly' | 'monthly';
    }>({
      query: ({ templateId, time_period = 'monthly' }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('time_period', time_period);
        return { url: `workflow-analytics/${templateId}/`, params: queryParams };
      },
      providesTags: (result, error, { templateId }) => [{ type: 'WorkflowAnalytics', id: templateId }],
    }),
    
    // Get workflow bottleneck analysis
    getWorkflowBottlenecks: builder.query<{ 
      template_id: string;
      template_name: string;
      bottlenecks: {
        level: number;
        level_name: string;
        average_time: string;
        percentage_of_total_time: number;
      }[];
    }[], void>({
      query: () => 'workflow-analytics/bottlenecks/',
      providesTags: [{ type: 'WorkflowAnalytics', id: 'BOTTLENECKS' }],
    }),
    
    // Get workflow trend analysis
    getWorkflowTrends: builder.query<{
      template_id: string;
      template_name: string;
      time_periods: string[];
      execution_counts: number[];
      average_completion_times: string[];
      approval_rates: number[];
      sla_compliance_rates: number[];
    }[], { 
      time_period: 'daily' | 'weekly' | 'monthly';
      limit?: number;
    }>({
      query: ({ time_period, limit = 12 }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('time_period', time_period);
        queryParams.append('limit', limit.toString());
        return { url: 'workflow-analytics/trends/', params: queryParams };
      },
      providesTags: [{ type: 'WorkflowAnalytics', id: 'TRENDS' }],
    }),
    
    // Get workflow rejection analysis
    getWorkflowRejectionAnalysis: builder.query<RejectionAnalysis[], WorkflowAnalyticsFilterParams | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return { url: 'workflow-analytics/rejections/', params: queryParams };
      },
      providesTags: [{ type: 'WorkflowAnalytics', id: 'REJECTIONS' }],
    }),
    
    // Generate workflow analytics report
    generateWorkflowAnalyticsReport: builder.mutation({
      query: (params: {
        workflow_template?: string;
        time_period: 'daily' | 'weekly' | 'monthly';
        date_from?: string;
        date_to?: string;
        format: 'pdf' | 'excel' | 'html';
        include_charts: boolean;
        include_bottleneck_analysis: boolean;
        include_rejection_analysis: boolean;
      }) => ({
        url: 'workflow-analytics/reports/',
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: { report_url: string }) => response,
    }),

    updateWorkflowTransaction: builder.mutation<UpdateWorkflowTransactionResponse, {id: string; formData: any}>({
      query: ({id, formData}) => ({
        url: `workflow-transactions/${id}/form-data/`,
        method: 'PATCH',
        body: {form_data: formData},
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'WorkflowTransaction', id: result.data.id },
            { type: 'WorkflowTransaction', id: 'LIST' },
          ]
          : [{ type: 'WorkflowTransaction', id: 'LIST' },],
    }),
    
    // Delete workflow transaction
    deleteWorkflowTransaction: builder.mutation<void, string>({
      query: (id) => ({
        url: `workflow-transactions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'WorkflowTransaction', id },
        { type: 'WorkflowTransaction', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetWorkflowTransactionsQuery,
  useGetWorkflowTransactionByIdQuery,
  useGetWorkflowTransactionStatusQuery,
  useGetWorkflowTransactionHistoryQuery,
  useGetWorkflowTransactionActiveLevelQuery,
  useGetWorkflowTransactionSlaQuery,
  useExportWorkflowTransactionHistoryMutation,
  useGetWorkflowAnalyticsQuery,
  useGetWorkflowAnalyticsByTemplateQuery,
  useGetWorkflowBottlenecksQuery,
  useGetWorkflowTrendsQuery,
  useGetWorkflowRejectionAnalysisQuery,
  useGenerateWorkflowAnalyticsReportMutation,
  useDeleteWorkflowTransactionMutation,
  useUpdateWorkflowTransactionMutation,
} = workflowTransactionApi;
